import type { CycleDesignerFormState } from '../types';

interface StepSetupProps {
  form: CycleDesignerFormState;
  onPatch: (patch: Partial<CycleDesignerFormState>) => void;
}

export function StepSetup({ form, onPatch }: StepSetupProps) {
  return (
    <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
      <div style={{ maxWidth: 640, width: '100%' }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', letterSpacing: '0.16em', color: 'var(--mustard)', textTransform: 'uppercase', marginBottom: 8 }}>
            New Cycle
          </div>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: '1.75rem', fontWeight: 300, color: 'var(--text-pri)', margin: 0 }}>
            What does this cycle need to resolve?
          </h2>
          <p style={{ fontSize: '1rem', color: 'var(--text-ter)', marginTop: 8, lineHeight: 1.45 }}>
            Define the strategic question and timeline before designing your ball paths.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.12em', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 6 }}>
              Cycle Name
            </label>
            <input
              value={form.cycleName}
              onChange={(e) => onPatch({ cycleName: e.target.value })}
              placeholder="e.g. Q3 2026 — Resource Alignment"
              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--rule-bright)', color: 'var(--text-pri)', padding: '10px 12px', fontSize: '1rem', outline: 'none' }}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.12em', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 6 }}>
              Strategic Intent <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              value={form.strategicIntent}
              onChange={(e) => onPatch({ strategicIntent: e.target.value })}
              rows={3}
              placeholder="Describe the central question or tension this cycle is meant to resolve."
              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--rule-bright)', color: 'var(--text-pri)', padding: '10px 12px', fontSize: '1rem', outline: 'none', resize: 'vertical' }}
            />
          </div>

          {/* Both dates are optional and always have been — nothing gates on them,
              and the cadence derivation returns relative labels ("Launch + 5d")
              whenever the anchoring date is absent. They read as required only
              because the labels never said otherwise, which made the per-level
              windows on the Guidance step look like they were contradicting a
              date range the designer had not actually committed to. */}
          <div>
            <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.12em', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 6 }}>Start Date <span style={{ color: 'var(--text-dim)' }}>· optional</span></label>
            <input type="date" value={form.startDate} onChange={(e) => onPatch({ startDate: e.target.value })} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--rule-bright)', color: 'var(--text-pri)', padding: '10px 12px', fontSize: '1rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.12em', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 6 }}>End Date <span style={{ color: 'var(--text-dim)' }}>· optional</span></label>
            <input type="date" value={form.endDate} onChange={(e) => onPatch({ endDate: e.target.value })} style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--rule-bright)', color: 'var(--text-pri)', padding: '10px 12px', fontSize: '1rem' }} />
          </div>
          <div style={{ gridColumn: '1 / -1', marginTop: -6, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
            Leave either blank to launch without a calendar. Response windows on the
            Guidance step still apply — the schedule just reads as
            &ldquo;Launch&nbsp;+&nbsp;5d&rdquo; instead of a date, and nothing is
            enforced against a deadline until you set one.
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.12em', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 6 }}>
              Calendar Anchor
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {([
                { value: 'start', title: 'Forward from start', hint: 'Each level’s date accumulates down from launch day.' },
                { value: 'lock', title: 'T-minus from end', hint: 'The last level lands on the lock date; every level above is counted back from it.' },
              ] as const).map((opt) => {
                const active = (form.cadenceAnchor ?? 'start') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onPatch({ cadenceAnchor: opt.value })}
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      background: active ? 'var(--bg-raised)' : 'var(--bg)',
                      border: `1px solid ${active ? 'var(--mustard)' : 'var(--rule-bright)'}`,
                      color: 'var(--text-pri)',
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{opt.title}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-ter)', marginTop: 2, lineHeight: 1.4 }}>{opt.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
